import { ERoomType } from "@/models/common/enums";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useCallback, useMemo } from "react";
import { SlotsReadableTO } from "@/models/detail";
import { IRoom } from "@/models/checkout";
import { useStore } from "@/store/index";
import { getExcludedDaysSession } from "../queries";

const useCalculatePrice = ({
    isPremium,
    startDate,
    tourId,
    rooms,
    slots,
}: {
    isPremium: boolean;
    startDate: string;
    tourId: string;
    rooms: IRoom[];
    slots: SlotsReadableTO[];
}) => {
    const dateFormat = "YYYY-MM-DD";
    const TotalRooms = useStore(state => state.checkoutState.orderData.rooms);

    const { data: daysExcluded } = useQuery({
        queryKey: ["exclusionDays", tourId],
        queryFn: () => getExcludedDaysSession(tourId),
    });

    // console.log("daysExcluded => ", daysExcluded);
    const { overrideDate } = useMemo(() => {
        const override =
            (isPremium
                ? daysExcluded?.overridePremium
                : daysExcluded?.overrideQuality) || [];
        const discounted = override.reduce((acc, value) => {
            return value.discount ? [...acc, value.day] : acc;
        }, [] as string[]);
        return { overrideDate: override, discountedDates: discounted };
    }, [daysExcluded, isPremium]);

    // console.log(overrideDate);

    const selectedSlot = useMemo(() => {
        return slots.find(slot =>
            moment(moment(startDate).format(dateFormat)).isBetween(
                moment(slot.startDate),
                moment(slot.endDate),
                undefined,
                "[]"
            )
        );
    }, [slots, startDate]);

    // console.log(selectedSlot);

    const overrideDateFind = useMemo(() => {
        const selectedOverride = overrideDate.find(value =>
            moment(startDate).isSame(value.day)
        );

        return selectedOverride?.roomsOverride ?? [];
    }, [overrideDate, startDate]);

    // console.log(overrideDateFind);

    const calculateSelectedSlot = useCallback(
        (item: IRoom) => {
            const idxSlot =
                item.type === ERoomType.SINGLE
                    ? 0
                    : item.type === ERoomType.DOUBLE
                    ? 1
                    : 2;

            if (overrideDateFind.length > 0) {
                return overrideDateFind.at(idxSlot);
            }

            return selectedSlot?.rooms.at(idxSlot);
        },
        [overrideDateFind, selectedSlot?.rooms]
    );

    const price = useMemo(() => {
        if (!rooms || !Array.isArray(rooms) || rooms.length === 0) {
            return 0;
        }
        return rooms.reduce((previousValue, currentValue) => {
            const roomCurrent = calculateSelectedSlot(currentValue);
            return roomCurrent
                ? previousValue + +roomCurrent.grossPrice
                : previousValue;
        }, 0);
    }, [calculateSelectedSlot, rooms]);

    const roomCurrent = useMemo(() => {
        if (
            !TotalRooms ||
            !Array.isArray(TotalRooms) ||
            TotalRooms.length === 0
        ) {
            // Se TotalRooms non è disponibile, usa rooms come fallback
            if (!rooms || !Array.isArray(rooms) || rooms.length === 0) {
                return [];
            }
            return rooms.map(res => calculateSelectedSlot(res));
        }
        return TotalRooms.map(res => calculateSelectedSlot(res));
    }, [calculateSelectedSlot, TotalRooms, rooms]);

    // React.useEffect(() => {
    //     rooms.forEach(room => {
    //         const roomCurrent = calculateSelectedSlot(room);
    //         const roomDetails = roomCurrent
    //             ? {
    //                   grossPrice: roomCurrent.grossPrice,
    //                   type: roomCurrent.type,
    //               }
    //             : {
    //                   grossPrice: "",
    //                   type: "",
    //               };

    //         dispatch({
    //             type: "SET_PRICE_ROOM",
    //             payload: roomDetails,
    //         });
    //     });
    // }, [calculateSelectedSlot, rooms]);

    return { price, calculateSelectedSlot, roomCurrent };
};

export default useCalculatePrice;
